'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Mindmap, MindmapLayoutDirection } from '@/features/mindmap/core/MindmapTypes';
import { updateLayoutDirection, updateNodeStyle, type MindmapNodeStylePatch } from '@/features/mindmap/core/MindmapState';
import type { SetDocWithHistory } from './UseMindmapHistory';

interface ToolbarNodeState {
  id: string;
  isRoot: boolean;
}

export interface UseMindmapToolbarOptions {
  doc: Mindmap;
  editingId: string | null;
  selectedRawIds: string[];
  selectedRawIdsRef: React.MutableRefObject<string[]>;
  viewportInteractingRef: React.MutableRefObject<boolean>;
  setDocWithHistory: SetDocWithHistory;
  disableToolbarPanGating: boolean;
}

export interface UseMindmapToolbarResult {
  toolbarNode: ToolbarNodeState | null;
  hoveredNodeId: string | null;
  previewMindmap: Mindmap | null;
  layoutPreview: MindmapLayoutDirection | null;
  hideToolbar: () => void;
  clearHoveredNode: () => void;
  handleNodeHoverChange: (nodeId: string, hovered: boolean) => void;
  handleStylePreview: (nodeId: string, patch: MindmapNodeStylePatch) => void;
  handleStyleCommit: (nodeId: string, patch: MindmapNodeStylePatch) => void;
  handleDirectionPreview: (direction: MindmapLayoutDirection) => void;
  handleDirectionCommit: (direction: MindmapLayoutDirection) => void;
  handleDirectionReset: () => void;
}

export function useMindmapToolbar({
  doc,
  editingId,
  selectedRawIds,
  selectedRawIdsRef,
  viewportInteractingRef,
  setDocWithHistory,
  disableToolbarPanGating,
}: UseMindmapToolbarOptions): UseMindmapToolbarResult {
  const [toolbarNode, setToolbarNode] = useState<ToolbarNodeState | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [previewMindmap, setPreviewMindmap] = useState<Mindmap | null>(null);
  const [layoutPreview, setLayoutPreview] = useState<MindmapLayoutDirection | null>(null);
  // Simplified: no delayed hide timers for toolbar visibility.

  const hideToolbar = useCallback(() => {
    setPreviewMindmap(null);
    setLayoutPreview(null);
    setToolbarNode(null);
  }, []);

  const showToolbarForNode = useCallback(
    (nodeId: string) => {
      const node = doc.nodes.find((n) => n.id === nodeId);
      if (!node) return;
      setPreviewMindmap(null);
      setToolbarNode({
        id: nodeId,
        isRoot: node.parentId == null,
      });
    },
    [doc.nodes]
  );

  // Hover start/end no longer affect visibility; selection drives it.

  const clearHoveredNode = useCallback(() => {
    setHoveredNodeId(null);
  }, []);

  const handleNodeHoverChange = useCallback(
    (nodeId: string, hovered: boolean) => {
      if (hovered) {
        setHoveredNodeId(nodeId);
        return;
      }

      setHoveredNodeId((current) => (current === nodeId ? null : current));
    },
    [
      selectedRawIdsRef,
    ]
  );

  const handleStylePreview = useCallback(
    (nodeId: string, patch: MindmapNodeStylePatch) => {
      const committedNode = doc.nodes.find((n) => n.id === nodeId);
      if (!committedNode) return;
      const working = previewMindmap ?? doc;
      const next = updateNodeStyle(working, nodeId, patch, { preview: true });
      const nextNode = next.nodes.find((n) => n.id === nodeId);
      if (!nextNode) {
        setPreviewMindmap(null);
        return;
      }
      const matchesCommitted = (['textColor', 'bgColor', 'borderColor', 'shape'] as const).every(
        (key) => (nextNode as any)[key] === (committedNode as any)[key]
      );
      setPreviewMindmap(matchesCommitted ? null : next);
    },
    [doc, previewMindmap]
  );

  const handleStyleCommit = useCallback(
    (nodeId: string, patch: MindmapNodeStylePatch) => {
      setDocWithHistory((prev) => updateNodeStyle(prev, nodeId, patch));
      setPreviewMindmap(null);
    },
    [setDocWithHistory]
  );

  const handleDirectionPreview = useCallback((direction: MindmapLayoutDirection) => {
    setLayoutPreview(direction);
  }, []);

  const handleDirectionCommit = useCallback(
    (direction: MindmapLayoutDirection) => {
      setDocWithHistory((prev) => updateLayoutDirection(prev, direction));
      setLayoutPreview(null);
    },
    [setDocWithHistory]
  );

  const handleDirectionReset = useCallback(() => {
    setLayoutPreview(null);
  }, []);

  useEffect(() => {
    if (!editingId) return;
    setHoveredNodeId(null);
    hideToolbar();
  }, [editingId, hideToolbar]);

  useEffect(() => {
    const hasSingleSelection = selectedRawIds.length === 1;
    const selectedId = hasSingleSelection ? selectedRawIds[0] : null;

    if (editingId) {
      return;
    }

    if (!hasSingleSelection || !selectedId) {
      if (toolbarNode) {
        hideToolbar();
      } else {
        setPreviewMindmap(null);
        setLayoutPreview(null);
      }
      return;
    }

    // Simplified: show toolbar when a single node is selected and not editing.
    // Guard to avoid infinite update loops by only updating when target changes.
    if (!toolbarNode || toolbarNode.id !== selectedId) {
      showToolbarForNode(selectedId);
    }
  }, [
    editingId,
    hideToolbar,
    selectedRawIds,
    showToolbarForNode,
    toolbarNode,
  ]);

  // No timers to clear on unmount.

  return {
    toolbarNode,
    hoveredNodeId,
    previewMindmap,
    layoutPreview,
    hideToolbar,
    clearHoveredNode,
    handleNodeHoverChange,
    handleStylePreview,
    handleStyleCommit,
    handleDirectionPreview,
    handleDirectionCommit,
    handleDirectionReset,
  };
}

export default useMindmapToolbar;
